import React, { useMemo, useEffect, useRef, useState } from "react";
import ModalComponent from "../../components/ModalComponent";
import * as Diff from "diff";

const VersionComparisonModal = ({ isOpen, onClose, comparisonData }) => {

  // ------------------------------
  // SAME STATES OF VersionComparisonTab
  // ------------------------------
  const baseContentRef = useRef(null);
  const targetContentRef = useRef(null);

  const [processedBaseHtml, setProcessedBaseHtml] = useState("");
  const [processedTargetHtml, setProcessedTargetHtml] = useState("");

  const isSyncingScroll = useRef(false);

  const decodeHtml = (html) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  };

  const baseHtml = useMemo(
    () => decodeHtml(comparisonData?.meta?.raw_base || ""),
    [comparisonData?.meta?.raw_base]
  );

  const targetHtml = useMemo(
    () => decodeHtml(comparisonData?.meta?.raw_target || ""),
    [comparisonData?.meta?.raw_target]
  );

  const getTextFromHtml = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  // ---------------------------------
  // HIGHLIGHT LOGIC FROM VersionComparisonTab
  // ---------------------------------
  const highlightDifferences = () => {
    if (!baseHtml || !targetHtml) return;

    const baseText = getTextFromHtml(baseHtml);
    const targetText = getTextFromHtml(targetHtml);

    const diff = Diff.diffWords(baseText, targetText);

    let basePosition = 0;
    let targetPosition = 0;
    const changedPositions = { base: [], target: [] };

    diff.forEach((part) => {
      const wordCount = part.value.split(/\s+/).filter((w) => w).length;

      if (part.removed) {
        changedPositions.base.push({
          startPos: basePosition,
          endPos: basePosition + wordCount,
        });
        basePosition += wordCount;
      } else if (part.added) {
        changedPositions.target.push({
          startPos: targetPosition,
          endPos: targetPosition + wordCount,
        });
        targetPosition += wordCount;
      } else {
        basePosition += wordCount;
        targetPosition += wordCount;
      }
    });

    const wrapSpecificWords = (html, ranges, className) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      let wordIndex = 0;

      const walk = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const words = node.textContent.split(/(\s+)/);
          const fragment = document.createDocumentFragment();

          words.forEach((word) => {
            const changed = ranges.some(
              (pos) =>
                wordIndex >= pos.startPos &&
                wordIndex < pos.endPos &&
                word.trim()
            );

            if (changed && word.trim()) {
              const mark = document.createElement("mark");
              mark.className = className;
              mark.textContent = word;
              fragment.appendChild(mark);
            } else {
              fragment.appendChild(document.createTextNode(word));
            }

            if (word.trim()) wordIndex++;
          });

          node.parentNode.replaceChild(fragment, node);
        } else {
          [...node.childNodes].forEach(walk);
        }
      };

      walk(doc.body);
      return doc.body.innerHTML;
    };

    const highlightedBase = wrapSpecificWords(
      baseHtml,
      changedPositions.base,
      "diff-removed"
    );

    const highlightedTarget = wrapSpecificWords(
      targetHtml,
      changedPositions.target,
      "diff-added"
    );

    setProcessedBaseHtml(highlightedBase);
    setProcessedTargetHtml(highlightedTarget);
  };

  // ---------------------------------
  // SYNC SCROLL
  // ---------------------------------
  const syncLeftScroll = (e) => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;

    const pct =
      e.target.scrollTop /
      (e.target.scrollHeight - e.target.clientHeight);

    targetContentRef.current.scrollTop =
      pct *
      (targetContentRef.current.scrollHeight -
        targetContentRef.current.clientHeight);

    setTimeout(() => {
      isSyncingScroll.current = false;
    }, 50);
  };

  const syncRightScroll = (e) => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;

    const pct =
      e.target.scrollTop /
      (e.target.scrollHeight - e.target.clientHeight);

    baseContentRef.current.scrollTop =
      pct *
      (baseContentRef.current.scrollHeight -
        baseContentRef.current.clientHeight);

    setTimeout(() => {
      isSyncingScroll.current = false;
    }, 50);
  };

  // ---------------------------------
  // ON LOAD → HIGHLIGHT
  // ---------------------------------
  useEffect(() => {
    if (baseHtml && targetHtml) {
      const timer = setTimeout(highlightDifferences, 80);
      return () => clearTimeout(timer);
    }
  }, [baseHtml, targetHtml]);

  if (!isOpen) return null;

  if (!comparisonData) {
    return (
      <ModalComponent
        show={isOpen}
        handleClose={onClose}
        title="Version Comparison"
        size="xl"
      >
        <p>No comparison data found</p>
      </ModalComponent>
    );
  }

  const { meta } = comparisonData;

  // ---------------------------------
  // RETURN FINAL MODAL WITH FULL TAB UI
  // ---------------------------------
  return (
    <ModalComponent
      show={isOpen}
      handleClose={onClose}
      title="Version Comparison"
      size="xl"
      footerButtons={[
        {
          label: "Close",
          variant: "secondary",
          onClick: onClose,
        },
      ]}
    >
      <div className="version-comparison-container" style={{ maxHeight: "80vh" }}>
        <div className="comparison-body">

          {/* LEFT OLD VERSION */}
          <div className="comparison-panel base-panel">
            <div className="panel-header">
              <h6>Version {meta.base_version}</h6>
              <span className="version-tag old">v{meta.base_version}</span>
            </div>

            <div
              className="panel-content"
              ref={baseContentRef}
              onScroll={syncLeftScroll}
              style={{ overflowY: "scroll", maxHeight: "70vh" }}
            >
              <div
                className="html-preview base-html"
                dangerouslySetInnerHTML={{
                  __html:
                    processedBaseHtml ||
                    "<p class='no-content'>No content available</p>",
                }}
              />
            </div>
          </div>

          {/* RIGHT NEW VERSION */}
          <div className="comparison-panel target-panel">
            <div className="panel-header">
              <h6>Version {meta.target_version}</h6>
              <span className="version-tag new">v{meta.target_version}</span>
            </div>

            <div
              className="panel-content"
              ref={targetContentRef}
              onScroll={syncRightScroll}
              style={{ overflowY: "scroll", maxHeight: "70vh" }}
            >
              <div
                className="html-preview target-html"
                dangerouslySetInnerHTML={{
                  __html:
                    processedTargetHtml ||
                    "<p class='no-content'>No content available</p>",
                }}
              />
            </div>
          </div>

        </div>
      </div>
    </ModalComponent>
  );
};

export default VersionComparisonModal;
